import {
  formatCurrency,
  formatDate,
  NoWrap,
  numberFormat,
  transformDailyData,
  transformMonthlyData,
  transformWeeklyData,
  transformYearlyData,
} from "../../utils/helpers";
import { TransactionChannelTag } from "../../components/common/registerUtils";
import { InputType } from "../../types/common";
import {
  DAILY_TRANSACTION_CHANNEL_SEPARATION,
  MONTHLY_TRANSACTION_CHANNEL_SEPARATION,
} from "../../utils/apis";

type ReportType = "daily" | "weekly" | "monthly" | "yearly";
type TotalType = "total_amount" | "total_count" | undefined;

const getTableApiForReportType = (reportType: ReportType): string =>
  reportType === "daily" || reportType === "weekly"
    ? DAILY_TRANSACTION_CHANNEL_SEPARATION
    : MONTHLY_TRANSACTION_CHANNEL_SEPARATION;

const getGraphApiForReportType = (reportType: ReportType): string =>
  getTableApiForReportType(reportType) + "graphic-data/";

export const useTransactionChannelSeparation = () => {
  return {
    reportTitle: "Transaction Summary",

    customApi: (reportType: ReportType) => getTableApiForReportType(reportType),

    customGraphApi: (reportType: ReportType) =>
      getGraphApiForReportType(reportType),

    tableData: (results: any[], reportType: ReportType) => {
      if (reportType === "weekly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const week = Math.ceil(item.day_of_month / 7);
          const key = `${week}-${item.channel}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              channel: item.channel,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.entries(grouped)
          .sort(([a], [b]) => {
            const [weekA, channelA] = a.split("-");
            const [weekB, channelB] = b.split("-");
            return (
              Number(weekA) - Number(weekB) || channelA.localeCompare(channelB)
            );
          })
          .map(([key, item]: [string, any]) => {
            const [week] = key.split("-");
            return {
              week_of_month: `Week ${week}`,
              total_amount: formatCurrency(item.total_amount),
              total_count: numberFormat(item.total_count),
              channel: <TransactionChannelTag channel={item.channel} />,
            };
          });
      }

      if (reportType === "yearly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const key = `${item.year_value}-${item.channel}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              channel: item.channel,
              year: item.year_value,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.values(grouped)
          .sort(
            (a: any, b: any) =>
              Number(a.year) - Number(b.year) ||
              a.channel.localeCompare(b.channel),
          )
          .map((item: any) => ({
            year_value: item.year,
            total_amount: formatCurrency(item.total_amount),
            total_count: numberFormat(item.total_count),
            channel: <TransactionChannelTag channel={item.channel} />,
          }));
      }

      // daily / monthly
      return results.map((item: any) => ({
        ...item,
        date: (
          <NoWrap>
            {reportType === "daily"
              ? formatDate(item.date)
              : item.month_name || item.date}
          </NoWrap>
        ),
        total_amount: formatCurrency(item.total_amount || "0"),
        total_count: numberFormat(item.total_count || "0"),
        channel: <TransactionChannelTag channel={item.channel} />,
      }));
    },

    tableColumns: (reportType: ReportType) => [
      ...(reportType === "daily" ? [{ id: "date", header: "Date" }] : []),
      ...(reportType === "monthly"
        ? [{ id: "month_name", header: "Month" }]
        : []),
      ...(reportType === "weekly"
        ? [{ id: "week_of_month", header: "Week" }]
        : []),
      ...(reportType === "yearly"
        ? [{ id: "year_value", header: "Year" }]
        : []),
      { id: "total_amount", header: "Total Amount", align: "right" },
      { id: "total_count", header: "Total Count" },
      {
        id: "channel",
        header: "channel",
        filter: {
          type: "select",
          options: [
            { id: "App&Web", name: "App & Web" },
            { id: "QRCode", name: "QR Code" },
            { id: "MerchantCode", name: "Merchant Code" },
            { id: "API", name: "API" },
          ],
        },
      },
    ],

    graphData: (
      results: any[],
      reportType: ReportType,
      totalType: TotalType,
    ) => {
      if (!results.length) return { xLabels: [], series: [], yLabel: "" };

      let transformed;
      if (reportType === "daily")
        transformed = transformDailyData(results, totalType);
      else if (reportType === "weekly")
        transformed = transformWeeklyData(results, totalType);
      else if (reportType === "yearly")
        transformed = transformYearlyData(results, totalType);
      else transformed = transformMonthlyData(results, totalType);

      return {
        ...transformed,
        yLabel: totalType === "total_amount" ? "Total Amount" : "Total Count",
      };
    },

    externalFilters: [
      {
        id: "start_date",
        header: "Date Range",
        type: "date" as InputType,
        rangeNames: {
          startKey: "start_date",
          endKey: "end_date",
        },
      },
    ],

    initialFilters: {
      channel: "API",
    },

    exportData: {
      /**
       * Returns the correct export endpoint for the active report type,
       * mirroring the same daily / monthly split used for data fetching.
       */
      getEndpoint: (reportType: ReportType) =>
        reportType === "daily" || reportType === "weekly"
          ? `${DAILY_TRANSACTION_CHANNEL_SEPARATION}export-to-csv`
          : `${MONTHLY_TRANSACTION_CHANNEL_SEPARATION}export-to-csv`,
      fileName: "transaction-channel-separation-report.xlsx",
      buttonText: "Export CSV",
    },

    totalTypeOptions: [
      { label: "Total Amount", value: "total_amount" },
      { label: "Total Count", value: "total_count" },
    ],

    showReportTypeFilter: true,
    showTotalTypeFilter: true,
    showMonthFilter: true,
    showYearFilter: true,
    defaultReportType: "monthly" as ReportType,
    defaultTotalType: "total_amount" as TotalType,
  };
};
